import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import SEO from "../components/SEO";
import { getCartProductTitle } from "../utils/productDisplay";
import NaverPayButton from "../components/NaverPayButton";
import { cartOrderSnapshot, getCartUnitPrice, getCartDeliveryFee } from "../utils/cartPricing";
import CartSyncStatus from "../components/CartSyncStatus";
import CartQuantityControl from "../components/CartQuantityControl";

const BagIcon = ({ className = "w-14 h-14" }) => (
  <svg aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 8.25h10.5l.75 12H6l.75-12Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V6a3 3 0 0 1 6 0v3" />
  </svg>
);

const PackageIcon = ({ className = "w-8 h-8" }) => (
  <svg aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 7.5 8.25 4.75 8.25-4.75M12 22V12.25M4.5 6.75 12 2.5l7.5 4.25v9.5L12 21l-7.5-4.75v-9.5Z" />
  </svg>
);

function getProductLink(item) {
  if (item.product_type === "page") return `/p/${item.product?.slug || item.product_id}`;
  if (item.product_type === "copier") return `/copier/${item.product_id}`;
  return `/product/${item.product_type}/${item.product_id}`;
}

function getProductName(item) {
  if (!item.product) return null;
  return getCartProductTitle(item, null);
}

function getProductImage(item) {
  return item.product?.image_url || null;
}

const getProductPrice = getCartUnitPrice;

export default function CartPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { cartItems, cartUpdating, cartError, syncCart, updateCartQuantity, removeFromCart } = useShop();
  const navigate = useNavigate();
  const beforeOrder = async () => {
    const result = await syncCart();
    if (result?.error) throw new Error(result.error);
    if (!result?.length) throw new Error("장바구니가 비어 있습니다.");
    if (result.some(item => item.product?.unavailable)) throw new Error("구매할 수 없는 상품을 장바구니에서 삭제해주세요.");
    return { expected_cart: cartOrderSnapshot(result) };
  };
  const handleCheckout = async () => {
    try {
      await beforeOrder();
      navigate("/checkout");
    } catch (err) {
      alert(err.message);
    }
  };

  if (!user) {
    return (
      <div className="max-w-[980px] mx-auto px-4 pt-20 pb-16 text-center">
        <SEO title="장바구니" path="/cart" />
        <div className="mb-4 flex justify-center text-[#1a4b8f]"><BagIcon /></div>
        <h1 className="text-2xl font-bold mb-2">{t.cart_heading}</h1>
        <p className="text-gray-500 mb-6">{t.cart_loginRequired}</p>
        <Link to="/login" state={{ from: "/cart" }} className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition">
          {t.cart_loginBtn}
        </Link>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="max-w-[980px] mx-auto px-4 pt-20 pb-16 text-center">
        <SEO title="장바구니" path="/cart" />
        <div className="mb-4 flex justify-center text-[#1a4b8f]"><BagIcon /></div>
        <CartSyncStatus />
        <h1 className="text-2xl font-bold mb-2">{t.cart_empty}</h1>
        <p className="text-gray-500 mb-6">{t.cart_emptySub}</p>
        <Link to="/store/toner-supplies" className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition">
          {t.cart_browse}
        </Link>
      </div>
    );
  }

  const productTotal = cartItems.reduce((sum, item) => {
    const price = getProductPrice(item);
    return sum + (price ? price * item.quantity : 0);
  }, 0);
  const deliveryTotal = cartItems.reduce((sum, item) => sum + getCartDeliveryFee(item), 0);
  const totalPrice = productTotal + deliveryTotal;
  const naverPayEligible = cartItems.length > 0 && cartItems.every((item) => (
    item.product_type === "page"
    && !item.product?.unavailable
    && item.product?.sale_type === "sale"
    && item.product?.mall_type !== "b2b"
    && Number(item.product?.price || 0) > 0
  ));

  return (
    <div className="max-w-[980px] mx-auto px-4 pt-20 pb-16">
      <SEO title="장바구니" path="/cart" />
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-3"><BagIcon className="w-8 h-8 text-[#1a4b8f]" /> {t.cart_heading} <span className="text-blue-600">({cartItems.length})</span></h1>

      <CartSyncStatus />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-3">
          {cartItems.map((item) => (
            <div key={item.id} className="bg-white border rounded-xl p-4 flex gap-4 hover:shadow-md transition">
              {/* Image */}
              <Link to={getProductLink(item)} className="shrink-0">
                {getProductImage(item) ? (
                  <img src={getProductImage(item)} alt={getProductName(item)} className="w-24 h-24 object-contain rounded-lg bg-gray-50" loading="lazy" />
                ) : (
                  <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500"><PackageIcon /></div>
                )}
              </Link>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <Link to={getProductLink(item)} className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition leading-snug break-words">
                  {item.product?.product_code && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mr-1.5 align-middle"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" /></svg>{item.product.product_code}</span>}
                  {getProductName(item) || t.cart_product}
                </Link>
                <p className="text-xs text-gray-400 mt-0.5">
                  {item.product_type === "page" ? (item.product?.store_category || "판매 상품") :
                   item.product_type === "sktec" ? t.cart_sktec :
                   item.product_type === "ikoffices" ? t.cart_rental :
                   item.product_type === "copier" ? t.cart_copier : t.cart_toner}
                </p>
                {item.product?.unavailable && <p className="text-xs text-red-600 mt-1">구매할 수 없는 상품입니다. 삭제 후 주문해주세요.</p>}
                {getProductPrice(item) && (
                  <p className="text-sm font-bold text-blue-600 mt-1">
                    {(getProductPrice(item) * item.quantity).toLocaleString()}{t.cart_won}
                    {item.quantity > 1 && <span className="text-xs text-gray-400 font-normal ml-1">({getProductPrice(item).toLocaleString()}{t.cart_won} × {item.quantity})</span>}
                  </p>
                )}

                {getCartDeliveryFee(item) > 0 && <p className="text-xs text-gray-500 mt-1">배송비 {getCartDeliveryFee(item).toLocaleString()}원</p>}
                {/* Quantity */}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <CartQuantityControl
                    itemId={item.id}
                    quantity={item.quantity}
                    productName={getProductName(item) || t.cart_product}
                    disabled={!!item.product?.unavailable}
                    onChange={quantity => updateCartQuantity(item.id, quantity)}
                  />
                  <button
                    type="button"
                    onClick={() => removeFromCart(item.id)}
                    className="ml-auto text-xs text-red-400 hover:text-red-600 transition"
                  >{t.cart_remove}</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white border rounded-xl p-5 sticky top-20">
            <h3 className="font-bold text-base mb-4">{t.cart_orderSummary}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>{t.cart_itemCount}</span>
                <span>{cartItems.length}{t.cart_count}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>{t.cart_totalQty}</span>
                <span>{cartItems.reduce((s, i) => s + i.quantity, 0)}{t.cart_count}</span>
              </div>
              {totalPrice > 0 && (
                <>
                <div className="flex justify-between text-gray-500"><span>상품금액</span><span>{productTotal.toLocaleString()}{t.cart_won}</span></div>
                <div className="flex justify-between text-gray-500"><span>배송비</span><span>{deliveryTotal.toLocaleString()}{t.cart_won}</span></div>
                <div className="flex justify-between font-bold text-lg pt-3 border-t mt-3">
                  <span>총 주문금액</span>
                  <span className="text-blue-600">{totalPrice.toLocaleString()}{t.cart_won}</span>
                </div>
                </>
              )}
            </div>
            <button
              onClick={handleCheckout}
              disabled={cartUpdating || !!cartError || cartItems.some(item => item.product?.unavailable)}
              className="w-full mt-4 py-3 bg-[#0071e3] text-white rounded-xl font-semibold hover:bg-[#005bb5] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.cart_checkout}
            </button>
            {naverPayEligible && (
              <NaverPayButton mode="cart" className="mt-4" disabled={cartUpdating || !!cartError || cartItems.some(item => item.product?.unavailable)} beforeOrder={beforeOrder} />
            )}
            <button
              onClick={() => navigate("/inquiry", { state: { items: cartItems } })}
              className="w-full mt-2 py-3 bg-white text-[#0071e3] border-2 border-[#0071e3] rounded-xl font-semibold hover:bg-[#0071e3]/5 transition"
            >
              {t.cart_inquiryBtn}
            </button>
            <p className="text-[11px] text-gray-400 text-center mt-2">
              {t.cart_phoneNote}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
